import { classList } from '@findigs/helpers/src/helpers/class-list';
import React, { useId, useState } from 'react';
import { FormInput } from '../form-input';
import type { FormInputProps } from '../form-input';
import { styles } from './form-input-password.module.scss';
import HideIcon from '../../images/form-hide-icon.inline.svg';
import ShowIcon from '../../images/form-show-icon.inline.svg';

export type FormInputPasswordProps = Omit<FormInputProps, 'suffix'> & {
  /**
   * Hides the reveal button and forcefully
   * disables the revealing of the password.
   *
   * This should always be set to `true` prior to submitting
   * a form, to avoid security issues related to browsers
   * saving passwords with their autofill mechanism, instead of
   * their credential storage mechanism.
   *
   * See: https://technology.blog.gov.uk/2021/04/19/simple-things-are-complicated-making-a-show-password-option/
   */
  disableReveal: boolean;

  /**
   * Controls whether the input should be automatically trimmed when
   * it loses focus (blur event)
   * @default false
   * */
  trim?: boolean;
};

/**
 * Extends `FormInput` with better support for passwords.
 * All `FormInput` props are forwarded.
 */
export const FormInputPassword = React.forwardRef(
  (
    {
      disableReveal,
      id,
      className,
      trim = false,
      ...props
    }: FormInputPasswordProps,
    forwardedRef: React.ForwardedRef<HTMLInputElement>
  ) => {
    const _id = useId();

    const [reveal, setReveal] = useState(false);
    const finalReveal = reveal && !disableReveal;

    const classes = classList(styles, className);
    return (
      <FormInput
        id={id ?? _id}
        type={finalReveal ? 'text' : 'password'}
        ref={forwardedRef}
        className={classes}
        trim={trim}
        suffix={
          disableReveal ? undefined : (
            <button
              type="button"
              className="reveal"
              onClick={() => setReveal((x) => !x)}
              aria-label={reveal ? 'Hide password' : 'Show password'}
              aria-controls={id ?? _id}
            >
              {reveal ? (
                <ShowIcon aria-hidden={true} />
              ) : (
                <HideIcon aria-hidden={true} />
              )}
            </button>
          )
        }
        autoComplete="off"
        {...props}
      />
    );
  }
);
FormInputPassword.displayName = 'FormInputPassword';
